import React from 'react';
import classnames from 'classnames';

import {useOverlayTriggerState} from '@react-stately/overlays';
import type {Node, Selection} from '@react-types/shared';
import type {TabListState} from '@react-stately/tabs';
import {mergeProps} from '@react-aria/utils';
import {useButton} from '@react-aria/button';

import type {AriaButtonProps} from '@react-types/button';

import {TypographySize, useTypography} from '@time-webkit/all/hooks/typography';
import {PopoverOverlay} from '@time-webkit/all/molecules';
import {CheckIcon, ChevronUpIcon} from '@time-webkit/all/icons';
import {ListBox, ListBoxItem} from '@time-webkit/all/atoms/listbox';

import styles from '../styles.module.css';

export interface ShowMoreProps {
    state: TabListState<Record<string, string>>;
    moreLabel: string;
    count: number;
}

export type ShowMoreButtonProps = {
    btnRef: React.RefObject<HTMLButtonElement>;
    hasSelected?: boolean;
} & AriaButtonProps<'button'>;

const ShowMoreButton = (props: ShowMoreButtonProps) => {
    const {buttonProps} = useButton(props, props.btnRef);
    const tabTypography = useTypography({size: TypographySize.BodyM});

    return (
        <button
            {...mergeProps(props, buttonProps)}
            ref={props.btnRef}
            className={classnames(tabTypography, styles.rowChipButton, styles.showMoreBtn, {
                [styles.active]: props.hasSelected,
            })}
        >
            {props.children}
        </button>
    );
};

const DEFAULT_OVERLAY_STATE = {};

export const ShowMore: React.FC<ShowMoreProps> = ({state, count, moreLabel}) => {
    const slicedCollection = Array.from(state.collection).slice(-count);
    const popoverState = useOverlayTriggerState(DEFAULT_OVERLAY_STATE);

    const handleSelect = (keys: Selection) => {
        const selected = Array.from(keys);

        if (selected[0]) {
            state.setSelectedKey(selected[0]);
        }
    };

    const hasSelected = slicedCollection.some((item) => state.selectedKey === item.key);

    return (
        <PopoverOverlay
            state={popoverState}
            placement='bottom right'
            renderTriggerElement={(triggerProps, ref: React.RefObject<HTMLButtonElement>, triggerState) => {
                return (
                    <ShowMoreButton
                        {...triggerProps}
                        btnRef={ref}
                        hasSelected={hasSelected}
                    >
                        <span>{`${moreLabel} ${count}`}</span>
                        <ChevronUpIcon
                            width={16}
                            height={16}
                            className={classnames(styles.arrow, triggerState.isOpen ? styles.arrowUp : styles.arrowDown)}
                        />
                    </ShowMoreButton>
                );
            }}
        >
            <ListBox
                items={slicedCollection}
                className={styles.dropdown}
                onSelectionChange={handleSelect}
                selectionMode='single'
                shouldFocusWrap={true}
            >
                {(item: Node<Record<string, string>>) => {
                    const isSelected = state.selectedItem.key === item.key;

                    return (
                        <ListBoxItem {...item}>
                            <div className={styles.listChipButton} onClick={() => popoverState.setOpen(false)}>
                                <span>{item.rendered}</span>
                                {isSelected && <CheckIcon width={16} height={16} className={styles.check} />}
                            </div>
                        </ListBoxItem>
                    );
                }}
            </ListBox>
        </PopoverOverlay>
    );
};
